import React, { ReactElement, Suspense } from 'react';
import { css } from '@emotion/react';
import Upload, { UploadProps } from '$/components/ui/Input/Upload';
import Form from '$/components/ui/Form';
import tw, { theme } from 'twin.macro';
import { MediaHint, MediaRefFragment } from '$/GraphQL/schema/schema';
import { Avatar, PlayIcon, Spinner } from '$/images/Icons';
import { colorWithAlpha, readableColorOnBackground } from '$/utils/colors';
import { EditableInput, EditableInputProps } from '$/components/ui/EditableInput';
import { SerializedStyles } from '@emotion/react';
import { isNotVideo } from '$/utils/string';
import styled from '@emotion/styled';
import { StyleProps } from '..';
import Imgix from 'react-imgix';

import type { ImgixParams, AttributeConfig } from 'react-imgix';
import {
  LocalAsset,
  PageBuilderState,
  PageMode,
  PBState,
} from '$/components/ui/Input/PageBuilder.types';
import { isEmptyMarkdownString } from '../EditableInput/utils';

import { ButtonProps } from '../Button/Button';
import { isLangRtl } from '$/shared/frontend/utils/i18n';
import { generateId } from '$/utils/number';
import { getVimeoSrc } from '$/utils/vimeo';
import { getYoutubeSrc } from '$/utils/youtube';
import { useScript } from '$/hooks/useScript';
import appConfig from '$/config';
import { cssDimensionsForRef } from '$/utils/image';
import { ImageUploadIcon } from '$/images/FontAwesomeIcons';
import { useImgixParams } from '$/hooks/useImgixParams';
import Button from '../Button';
import { CTAButtonRenderProps } from './CTAButton/types';

const CTAButton = React.lazy(() => import('./CTAButton/CTAButton'));

interface MediaProps {
  value?: MediaRefFragment;
  sizes?: string;
  roundedCorners?: boolean;
  width?: number;
  height?: number;
  allowVideo?: boolean;
  prefetch?: boolean;
  playsinline?: boolean;
  asset: LocalAsset;
  setValidVideo?: (val: boolean) => void;
  playerProps?: {
    muted?: boolean;
    playing?: boolean;
    hideControls?: boolean;
    loop?: boolean;
  };
  imgProps?: ImgixParams;
  isVideoBackground?: boolean;
  placeholderSelector?: string;
  state?: PBState;
}

enum VideoType {
  Youtube = 'youtube',
  Vimeo = 'vimeo',
}

const CoverImage: React.FC<{
  playerId: string;
  imageUrl: string;
  tech: string;
  roundedCorners?: boolean;
}> = ({ playerId, imageUrl, tech, roundedCorners }) => {
  return (
    <div
      id={`cover-${playerId}`}
      className='absolute top-0 left-0 w-full h-full flex justify-center items-center bg-cover z-1'
      css={css`
        border-radius: ${roundedCorners ? '1rem' : '0'};
        background: url(${imageUrl}) no-repeat center center;
        button {
          background-color: ${tech === VideoType.Youtube ? '#ff0000' : '#00adef'};
          transition: all 0.5s;
        }
      `}
    >
      <button
        type='button'
        id={`play-${playerId}`}
        className='rounded-lg w-6e h-3_5e flex justify-center items-center'
      >
        <PlayIcon className='w-2e text-white' />
      </button>
    </div>
  );
};

interface VideoProps {
  id?: string;
  videoUrl: string;
  width: number;
  playsinline?: boolean;
  playerProps?: {
    muted?: boolean;
    playing?: boolean;
    hideControls?: boolean;
    loop?: boolean;
  };
  setValidVideo?: (val: boolean) => void;
}

const VimeoVideo: React.FC<VideoProps> = ({ id, videoUrl, playsinline, playerProps }) => {
  const videoSrc = getVimeoSrc(videoUrl);

  return (
    <iframe
      id={id}
      width='100%'
      height='100%'
      allowFullScreen
      frameBorder='0'
      allow='autoplay; encrypted-media; fullscreen;'
      src={`https://player.vimeo.com/video/${videoSrc?.srcID}?&${
        playerProps?.hideControls ? 'controls=0' : 'controls=1'
      }&quality=auto&responsive=1&title=0&portrait=0&byline=0&${
        playsinline ? 'playsinline=1' : 'playsinline=0'
      }&${playerProps?.loop ? 'loop=1' : 'loop=0'}&autopause=0&${
        playerProps?.playing ? 'autoplay=1' : 'autoplay=0'
      }&${playerProps?.muted ? 'muted=1' : 'muted=0'}&api=1`}
    />
  );
};

const YouTubeVideo: React.FC<VideoProps> = ({ id, videoUrl, playsinline, playerProps }) => {
  const videoSrc = getYoutubeSrc(videoUrl);

  return (
    <iframe
      id={id}
      width='100%'
      height='100%'
      allowFullScreen
      frameBorder='0'
      allow='autoplay; encrypted-media; fullscreen;'
      src={`https://www.youtube.com/embed/${videoSrc?.srcID}?playlist=${videoSrc?.srcID}&${
        playerProps?.loop ? 'loop=1' : 'loop=0'
      }&${playerProps?.hideControls ? 'controls=0' : 'controls=1'}&${
        playsinline ? 'playsinline=1' : 'playsinline=0'
      }&${playerProps?.playing ? 'autoplay=1' : 'autoplay=0'}&${
        playerProps?.muted ? 'mute=1' : 'mute=0'
      }&rel=0&quality=auto&responsive=1&title=0&disablekb=1&iv_load_policy=3&byline=0&autopause=0&showinfo=0&enablejsapi=1`}
    />
  );
};

const EmbedVideoScript: React.FC<{ embedScript: string }> = ({ embedScript }) => {
  useScript(embedScript, appConfig.isSSR);
  return appConfig.isSSR ? <script dangerouslySetInnerHTML={{ __html: embedScript }} /> : null;
};

export const VideoPlayer: React.FC<StyleProps<MediaProps>> = ({
  value,
  roundedCorners,
  asset,
  setValidVideo,
  playsinline,
  width,
  playerProps,
  isVideoBackground,
  placeholderSelector,
  className,
}) => {
  const [id] = React.useState(generateId());
  width = width ?? asset!.container!.width;

  const videoUrl = value?.videoUrl;
  const { tech } = React.useMemo(() => {
    return videoUrl?.indexOf('vimeo.com') !== -1
      ? { tech: VideoType.Vimeo, type: 'video/vimeo' }
      : { tech: VideoType.Youtube, type: 'video/youtube' };
  }, [videoUrl]);

  const containerPlayerId = `video-player-${id}`;
  const playerId = `video-${id}`;

  const embedScript = React.useMemo(() => {
    return `
      (function() {
        try {
          var tech = '${tech}';
          var isVideoBackground = Boolean(${isVideoBackground ? '1' : '0'});
          var containerPlayer = document.getElementById('${containerPlayerId}');
          var player = document.getElementById('${playerId}');
          var playButton = document.getElementById('play-${playerId}');
          var coverImage = document.getElementById('cover-${playerId}');

          if (!containerPlayer || !player || typeof window === 'undefined') return;

          function onResizeVideo() {
            if (!isVideoBackground) return;

            var width = window.innerWidth;
            var height = ${asset.container?.height} ?? width * (9 / 16);
            if (width / height > 16 / 9) {
              height = width * (9 / 16);
            }
            containerPlayer.style.width = width + 'px';
            containerPlayer.style.height = height + 'px';
          };

          onResizeVideo();
          window.addEventListener('resize', function() {
            onResizeVideo();
          });

          if (playButton) {
            playButton.addEventListener('click', function() {
              var src = player.src;
              player.src = src.replace('autoplay=0', 'autoplay=1');
              if (coverImage) {
                setTimeout(function() {
                  coverImage.style.transition = 'opacity 1s, visibility 1s';
                  coverImage.style.opacity = '0';
                  coverImage.style.visibility = 'hidden';
                }, 300);
              }
            });
          }

          player.addEventListener('load', function() {
            setTimeout(function() {
              var placeholder = document.querySelector('${placeholderSelector}');
              if (placeholder) placeholder.style.transition = 'opacity 1s';
              if (placeholder) placeholder.style.opacity = '0';
            }, 300);
          });

          document.addEventListener('visibilitychange', function() {
            if (!isVideoBackground) return;

            if (tech === '${VideoType.Vimeo}') {
              if (document.visibilityState !== 'visible') {
                if (!player || !player.contentWindow) return;
                player.contentWindow.postMessage(
                  { method: 'pause' },
                  '*'
                );
              } else {;
                player.src = player.src;
              }
              return;
            }

            if (!player || !player.contentWindow) return;
            player.contentWindow.postMessage(
              JSON.stringify({
                event: 'command',
                func: document.visibilityState === 'visible' ? 'playVideo' : 'pauseVideo'
              }),
              '*'
            );
          });
        } catch (e) {
          console.error(e);
        }
      }
    )();
  `;
  }, [
    asset.container?.height,
    containerPlayerId,
    isVideoBackground,
    placeholderSelector,
    playerId,
    tech,
  ]);

  if (!videoUrl) {
    console.debug('No video url provided', value);
    return null;
  }

  if (![VideoType.Youtube, VideoType.Vimeo].includes(tech)) return null;

  return (
    <div
      id={containerPlayerId}
      className={`relative z-image-or-video ${className}`}
      css={css`
        overflow: hidden;
        width: ${width}px;
        height: ${width * 0.5625}px;
        border-radius: ${roundedCorners ? '1rem' : '0'};
        & iframe {
          border-radius: ${roundedCorners ? '1rem' : '0'};
          position: absolute;
          top: 0;
          left: 0;
        }
        @media only screen and (max-width: ${theme`screens.md`}) {
          width: 100% !important;
          height: 100% !important;
        }
        padding-bottom: 56.25%; /* 16:9 ratio or we can use padding-top */
      `}
    >
      {value?.imageUrl && !playerProps?.playing && (
        <CoverImage
          playerId={playerId}
          imageUrl={value.imageUrl}
          roundedCorners={roundedCorners}
          tech={tech}
        />
      )}

      {tech === VideoType.Vimeo && (
        <VimeoVideo
          id={playerId}
          videoUrl={videoUrl}
          width={width}
          playsinline={playsinline}
          playerProps={playerProps}
          setValidVideo={setValidVideo}
        />
      )}

      {tech === VideoType.Youtube && (
        <YouTubeVideo
          id={playerId}
          videoUrl={videoUrl}
          width={width}
          playsinline={playsinline}
          playerProps={playerProps}
          setValidVideo={setValidVideo}
        />
      )}
      <EmbedVideoScript embedScript={embedScript} />
    </div>
  );
};

export const ImageOrVideo: React.FC<StyleProps<MediaProps>> = ({
  className,
  value,
  roundedCorners,
  allowVideo,
  asset,
  sizes,
  prefetch,
  playsinline,
  playerProps,
  imgProps,
  state,
  isVideoBackground,
  placeholderSelector,
}) => {
  const [validVideo, setValidVideo] = React.useState(
    () => value?.videoUrl && !isNotVideo(value.videoUrl)
  );

  React.useEffect(() => {
    setValidVideo(value?.videoUrl && !isNotVideo(value?.videoUrl));
  }, [value]);

  const imgixParams = useImgixParams(asset, value, imgProps);

  if (allowVideo && validVideo) {
    return (
      <VideoPlayer
        className={className}
        roundedCorners={roundedCorners}
        asset={asset}
        value={value}
        setValidVideo={setValidVideo}
        playerProps={playerProps}
        playsinline={playsinline}
        allowVideo={allowVideo}
        isVideoBackground={isVideoBackground}
        placeholderSelector={placeholderSelector}
      />
    );
  }

  let imgClass = '';
  let attributeConfig: AttributeConfig = {};
  if (prefetch) {
    /* DD: I don't seem to have a way to build an imagesrcset to preload the right file */
    // preloadUrl = buildURL(value?.imageUrl ?? '', { fit: 'crop', auto: 'format', ...imgProps });
  } else {
    imgClass = 'lazyload';
    if (state?.mode === PageMode.public) {
      attributeConfig = {
        src: 'data-src',
        srcSet: 'data-srcset',
        sizes: 'data-sizes',
      };
    }
  }

  return (
    <Imgix
      className={`${className} ${imgClass} w-full md:w-auto`}
      src={value?.imageUrl ?? 'https://assets.ewebinar.com/placeholder.png'}
      css={css`
        ${cssDimensionsForRef(value, {
          maxWidth: asset.container!.width,
          maxHeight: asset.container!.height ?? undefined,
        })}
        ${roundedCorners ? 'border-radius: 1rem;' : ''};
        ${asset.fit ? `object-fit: ${asset.fit};` : ''}
        object-position: ${asset.position ?? 'center center'};
      `}
      imgixParams={imgixParams}
      sizes={sizes}
      width={sizes ? undefined : asset.container!.width}
      height={sizes ? undefined : asset.container!.height ?? undefined}
      attributeConfig={attributeConfig}
      htmlAttributes={{ alt: value?.alt ?? '' }}
    />
  );
};

export const ImageForRef: React.FC<StyleProps<MediaProps>> = ({
  className,
  value,
  imgProps,
  sizes,
  asset,
}) => {
  const imgixParams = useImgixParams(asset, value, imgProps);

  if (!value) return null;

  return (
    <Imgix
      className={className}
      src={value?.imageUrl}
      imgixParams={imgixParams}
      sizes={sizes}
      width={sizes ? undefined : asset.container.width}
      height={sizes ? undefined : asset.container.height ?? undefined}
      htmlAttributes={{ alt: value?.alt ?? undefined }}
    />
  );
};

type PageBuilderImageOrVideoProps = Omit<UploadProps, 'asset' | 'allowVideo'> & {
  asset: LocalAsset;
  state: PBState;
  prefetch?: boolean;
  placeholder?: ReactElement;
  disallowVideo?: boolean;
  imgProps?: ImgixParams;
  renderEmpty?: () => JSX.Element;
};

type PageBuilderAvatarProps = Omit<PageBuilderImageProps, 'name'>;

interface PageBuilderTextEditProps extends EditableInputProps {
  tagName?: string;
  state: PBState;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  offsetCss?: SerializedStyles;
  isRtl?: boolean;
}

type PageBuilderImageProps = Omit<
  PageBuilderImageOrVideoProps,
  'value' | 'defaultValue' | 'setValue' | 'allowVideo' | 'onChange'
> & {
  value?: MediaRefFragment | null;
  onChange?: (value: MediaRefFragment | undefined | null) => void;
};

interface EditableButtonProps extends ButtonProps {
  state: PageBuilderState;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  name: string;
  className?: string;
  'data-text'?: string;
  placeholder?: string;
  defaultValue?: string;
  id?: string;
  maxChars?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: React.FC<any>;
  inputProps?: Record<string, string>;
  linkToolbarDisabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface PageBuilderComponent extends React.FC<any> {
  Avatar: React.FC<PageBuilderAvatarProps>;
  Image: React.FC<PageBuilderImageProps>;
  ImageOrVideo: React.FC<PageBuilderImageOrVideoProps>;
  TextEdit: React.FC<PageBuilderTextEditProps>;
  Button: React.FC<EditableButtonProps>;
  CardButton: React.FC<EditableButtonProps>;
}

const PageBuilder: PageBuilderComponent = (props) => {
  return props.children;
};

PageBuilder.Button = ({
  onClick,
  state,
  placeholder,
  name,
  className,
  defaultValue,
  maxChars,
  as,
  inputProps,
  linkToolbarDisabled,
  theme,
  ...props
}): ReactElement => {
  const Btn = as ?? Button.rounded;

  if (!theme) {
    theme = {
      fg: readableColorOnBackground(state.ewebinar.actionColor),
      fgHover: readableColorOnBackground(state.ewebinar.actionColor),
      bg: state.ewebinar.actionColor,
      bgHover: state.ewebinar.actionColor,
    };
  }

  return (
    <Btn
      disabled={state.mode === PageMode.edit}
      onClick={state.mode !== PageMode.edit ? onClick : undefined}
      className={className}
      theme={theme}
      css={[
        css`
          ${tw`flex justify-center items-center text-lg appearance-none outline-none`}
          font-family: '${state.ewebinar.font ?? 'unset'}';
          border-radius: ${state.ewebinar.borderRadius}px;
        `,
        state?.mode === PageMode.edit
          ? css`
              &:disabled {
                opacity: 1 !important;
              }
              .public-DraftEditorPlaceholder-root {
                color: ${colorWithAlpha(theme.fg, 0.6, { bgColor: state.ewebinar.primaryColor })};
              }
              position: relative;
              .error-message {
                position: absolute !important;
                display: table !important;
                top: 100%;
                left: 50%;
                transform: translateX(-50%);
                z-index: 10;
                width: 100%;
                word-break: normal;
              }
            `
          : undefined,
      ]}
      {...props}
    >
      <Form.Field
        name={name}
        component={
          <PageBuilder.TextEdit
            {...inputProps}
            placeholder={props['data-text'] ?? placeholder}
            tagName='span'
            offsetCss={css`
              margin-left: -10px;
              margin-right: -10px;
              left: 0;
            `}
            showFocusBox
            disabled={state?.mode !== PageMode.edit}
            state={state}
            isRtl={isLangRtl(state.ewebinar.viewerRoomSettings.language ?? 'en')}
            maxChars={maxChars}
            linkToolbarDisabled={linkToolbarDisabled}
          />
        }
        className='outline-none mb-0 w-auto'
        defaultValue={state?.mode !== PageMode.edit ? defaultValue : defaultValue ?? ''}
      />
    </Btn>
  );
};
PageBuilder.Button.displayName = 'PageBuilderButton';

PageBuilder.CardButton = ({ className, ...props }): ReactElement => {
  const ewebinar = props.state.ewebinar;
  const fg = readableColorOnBackground(ewebinar.actionColor);

  return (
    <PageBuilder.Button
      theme={{
        bg: ewebinar.actionColor,
        bgHover: ewebinar.actionColor,
        fg,
        fgHover: fg,
      }}
      className={className}
      css={[
        tw`py-3/4e rounded-full`,
        css`
          min-width: 12rem;
          border-radius: ${ewebinar.borderRadius}px;
        `,
      ]}
      linkToolbarDisabled={true}
      {...props}
    />
  );
};
PageBuilder.CardButton.displayName = 'CardBuilderButton';

const PBImageOrVideo: React.FC<PageBuilderImageOrVideoProps> = ({
  className,
  value,
  renderEmpty,
  disallowVideo,
  onChange,
  state,
  ...props
}): JSX.Element | null => {
  const onValueChanged = React.useCallback(
    (value) => {
      onChange?.(value);
    },
    [onChange]
  );

  return (
    <Upload
      {...props}
      onChange={onValueChanged}
      mode={state.mode}
      allowVideo={!disallowVideo}
      value={value}
      className={`relative flex flex-shrink-1 ${className}`}
      css={css`
        ${tw`z-page-section`}
        ${props.roundedCorners ? 'border-radius: 1rem !important;' : ''}
        .video-js {
          border-radius: ${props.roundedCorners ? '1rem' : '0'};
          overflow: hidden;
          picture {
            & > img {
              border-radius: ${props.roundedCorners ? '1rem' : '0'};
            }
          }
        }
      `}
    >
      <Upload.Loading />
      <Upload.Success>
        <ImageOrVideo
          playsinline
          allowVideo={!disallowVideo}
          roundedCorners={props.roundedCorners}
          state={state}
          {...props}
        />
      </Upload.Success>
      <Upload.Empty>
        {state.mode === PageMode.edit ? (
          renderEmpty ? (
            renderEmpty()
          ) : (
            <div
              className='flex flex-col align-center justify-center pageBuilderUpload'
              css={css`
                width: ${props.asset.container!.width}px;
                height: ${props.asset.container!.height}px;
              `}
            >
              {props.placeholder || (
                <div
                  className={`text-coral-1 text-center flex flex-col w-full items-center justify-center cursor-pointer`}
                >
                  <ImageUploadIcon className='flex w-8 h-8 mb-4 text-color-brand' />
                  <div>
                    <span className='font-body text-md underline'>
                      Add an image{!disallowVideo && ' or video'}
                    </span>
                    {props.error && (
                      <div className='font-body text-md mt-2 text-coral-1'>${props.error}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          <div />
        )}
      </Upload.Empty>
    </Upload>
  );
};
PageBuilder.ImageOrVideo = PBImageOrVideo;
PageBuilder.ImageOrVideo.displayName = 'PageBuilderImageOrVideo';

const PBImage: React.FC<PageBuilderImageProps> = (props): JSX.Element | null => {
  return <PBImageOrVideo {...props} disallowVideo />;
};
PageBuilder.Image = PBImage;
PageBuilder.Image.displayName = 'PageBuilderImage';

PageBuilder.TextEdit = styled<React.FC<PageBuilderTextEditProps>>(
  ({
    state,
    offsetCss,
    value = '',
    ctaConfig,
    isRtl,
    disabled: _, // ignore to not override local disabled props
    ...props
  }): React.ReactElement | null => {
    if (isEmptyMarkdownString(value) && state.mode !== PageMode.edit) {
      return null;
    }
    const ctaConfigs = React.useMemo(() => {
      if (!ctaConfig) return undefined;
      return {
        ...ctaConfig,
        renderCTA: (props: CTAButtonRenderProps): React.ReactNode => {
          return (
            <Suspense>
              <CTAButton {...props} {...ctaConfig} />
            </Suspense>
          );
        },
      };
    }, [ctaConfig]);

    return (
      <EditableInput
        disabled={state?.mode !== PageMode.edit}
        isPreview={state?.mode === PageMode.preview}
        showFocusBox
        showInlineToolbar
        textDirectionality={isRtl ? 'RTL' : 'LTR'}
        ctaConfig={ctaConfigs}
        css={[
          css`
            outline: none;
            word-break: break-word;
            min-width: 2rem;
            min-height: 1rem;
          `,
          state.mode === PageMode.edit &&
            css`
              ${tw`
              rounded-input
              border-color-pagebuilder-frame
              `}
              border: 2px dashed ${theme`colors.color.input-box`};
            `,
          state.mode === PageMode.edit &&
            css`
              position: relative;
              padding-left: 10px;
              padding-right: 10px;
            `,
          state.mode === PageMode.edit && offsetCss,
          state.mode === PageMode.edit &&
            css`
              &:empty:not(:focus):before {
                opacity: 0.4;
                content: attr(data-text);
                -webkit-pointer-events: none;
                -moz-pointer-events: none;
                -ms-pointer-events: none;
                pointer-events: none;
              }
              &:focus {
                box-shadow: 0 0 2px 2px #537175;
              }
            `,
        ]}
        {...props}
        value={value}

        // onBlur={e => {
        //   !e.target.innerText && props.onChange(props.defaultValue)
        // }}
      />
    );
  }
)``;
PageBuilder.TextEdit.displayName = 'PageBuilderTextEdit';

interface ProfileImageProps extends Omit<React.HTMLProps<HTMLDivElement>, 'value'> {
  value?: MediaRefFragment | null;
  asset: LocalAsset;
}

const ProfileImage: React.FC<ProfileImageProps> = ({ value, asset }) => {
  const imgixParams = useImgixParams(
    asset,
    value,
    /* These can be overriden if crop is specified in value (MediaRef) */ {
      ar: 1,
      faceindex: 1,
      facepad: 5,
      mask: 'ellipse',
      fit: 'facearea',
      w: asset.container.width,
      h: asset.container.height ?? undefined,
    }
  );

  return (
    <Imgix
      className='lazyload'
      src={value?.imageUrl ?? 'https://assets.ewebinar.com/placeholder.png'}
      css={css`
        ${tw`rounded-full bg-white shadow`}
        border: 4px solid #fff;
        object-fit: cover;
        width: ${asset.container.width}px;
        height: ${asset.container.height}px;
      `}
      imgixParams={imgixParams}
      width={asset.container.width}
      height={asset.container.height ?? undefined}
      htmlAttributes={{ alt: value?.alt ?? '' }}
    />
  );
};

PageBuilder.Avatar = ({ state, value, ...props }): JSX.Element | null => {
  return (
    <Upload
      {...props}
      asset={{ ...props.asset, container: { ...props.asset.container, hint: MediaHint.Profile } }}
      mode={state.mode}
      value={value}
      css={css`
        border: none !important;
      `}
    >
      <Upload.Loading>
        <div
          className='avatar'
          css={css`
            ${tw`flex bg-white rounded-full items-center justify-center`}
            width: ${props.asset!.container!.width}px;
            height: ${props.asset!.container!.height}px;
          `}
        >
          <Spinner className='h-2e' />
        </div>
      </Upload.Loading>
      <Upload.Success className='rounded-full overflow-hidden'>
        <ProfileImage asset={props.asset} value={value} />
      </Upload.Success>
      <Upload.Empty>
        <div
          className='avatar image-show'
          css={css`
            ${tw`rounded-full overflow-hidden bg-white shadow flex justify-center items-center`}
            border: 4px solid #fff;
            width: ${props.asset!.container!.width}px;
            height: ${props.asset!.container!.height}px;
          `}
        >
          <Avatar
            className='avatar'
            css={css`
              height: 66%;
            `}
          />
        </div>
      </Upload.Empty>
    </Upload>
  );
};

PageBuilder.Avatar.displayName = 'PageBuilderAvatar';

export default PageBuilder;
